import { CatalogTransferView } from '@Entities/rightsholder/catalog-transfer-view'
import { TableRowData } from '@Components/ViewWork/Table'
import React from 'react'
import { ICONS } from '@Const/common'
import Button from '@App/kit/components/Buttons/Button'
import Navigation from '@App/navigation'
import {
    getCatalogTransferDecisionForMember,
    isCatalogTransferDecisionFinal,
} from '@App/helpers/get-catalog-transfer-decision'
import CatalogTransferStatusPill from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferStatusPill'

export const buildTableRows = (
    transfers: CatalogTransferView[] | undefined,
    opts: {
        getMyPublisher: (t: CatalogTransferView) => any
        getTheirPublisher: (t: CatalogTransferView) => any
        getDateHeader: (t: CatalogTransferView) => string
        getDateValue: (t: CatalogTransferView) => string
        onDeletedClicked?: (t: CatalogTransferView) => void
        rightsholderId: number
        isReviewTab?: boolean
        isDisabled?: boolean
    }
): TableRowData[] | undefined => {
    if (!transfers) {
        return undefined
    }

    return transfers.map((transfer) => {
        const sourcePublisherNames = transfer.sourcePublishers?.length
            ? transfer.sourcePublishers.map((publisher) => publisher.publisherName).join(', ')
            : undefined
        const cells: React.ReactNode[] = [
            // --- Publisher Block ---
            <div className="publishers" key="publisher-block">
                <div>{opts.getMyPublisher(transfer)?.publisherName ?? '[SELECT PUBLISHER]'}</div>
                <div>
                    {transfer.catalogTransferType === 'ACQUISITION' ? 'Acquiring from' : 'Relinquishing from'}{' '}
                    <strong>
                        {transfer.catalogTransferType === 'ACQUISITION'
                            ? (sourcePublisherNames ??
                              opts.getTheirPublisher(transfer)?.publisherName ??
                              '[SELECT PUBLISHER]')
                            : (opts.getTheirPublisher(transfer)?.publisherName ?? '[SELECT PUBLISHER]')}
                    </strong>
                </div>
                <div>
                    Catalog Transfer ID: <strong>{transfer.id}</strong>
                </div>
                {transfer.writerNames !== null && (
                    <div>
                        Writer(s): <strong>{transfer.writerNames}</strong>
                    </div>
                )}
            </div>,

            // --- Date Block ---
            <div key="date-block">
                <div>{opts.getDateHeader(transfer)}</div>
                <div>
                    <strong>{opts.getDateValue(transfer)}</strong>
                </div>
            </div>,
        ]

        cells.push(
            <div key="action-needed">
                {transfer.catalogTransferStatus === 'CREATING' && (
                    <>
                        <div>Action Needed</div>
                        <div>{calculateActionNeeded(transfer)}</div>
                    </>
                )}
            </div>
        )

        // --- Works count ---
        cells.push(
            <div className="double-line" key="works-block">
                <div>Works</div>
                <div>
                    <strong>{transfer.workCount}</strong>
                </div>
            </div>
        )

        const canReviewActions = getCanReviewActions(transfer, opts)

        cells.push(
            <div className="centered" key="status-block">
                <div data-testid={`catalog-transfer-status-${transfer.id}`}>
                    <CatalogTransferStatusPill transfer={transfer} canReviewActions={canReviewActions} />
                </div>
            </div>
        )

        // --- Continue / Review Button ---
        const reviewButtonLabel = opts.isReviewTab ? (canReviewActions ? 'Review Request' : 'View') : 'Review'
        const reviewButtonType = opts.isReviewTab && canReviewActions ? 'primary-small' : 'secondary-small'

        cells.push(
            <div className="centered" key="button-block">
                {transfer.catalogTransferStatus === 'CREATING' ? (
                    <Button
                        mainColor
                        typeLink
                        type="primary-small"
                        linkTo={Navigation.rightholder.catalogTransfer.wizard(opts.rightsholderId, transfer.id)}
                    >
                        Continue
                    </Button>
                ) : (
                    <Button
                        mainColor
                        typeLink
                        type={reviewButtonType}
                        linkTo={Navigation.rightholder.catalogTransfer.view(opts.rightsholderId, transfer.id)}
                    >
                        {reviewButtonLabel}
                    </Button>
                )}
            </div>
        )

        // --- Delete Icon ---
        cells.push(
            <div className="centered" key={`delete-block-${transfer.id}`}>
                {transfer.catalogTransferStatus === 'CREATING' && opts.onDeletedClicked && (
                    <button
                        className="delete-button pointer"
                        type="button"
                        aria-label="Delete"
                        disabled={opts.isDisabled}
                        onClick={() => opts.onDeletedClicked?.(transfer)}
                    >
                        <img src={ICONS.trashCan} alt="Delete" />
                    </button>
                )}
            </div>
        )

        return { cells, removable: false }
    })
}

const calculateActionNeeded = (transfer: CatalogTransferView) => {
    if (transfer.isPartialTransfer === null || transfer.isPartialTransfer === undefined) {
        return 'Select Transfer Type'
    } else if (!transfer.sourcePublishers?.length) {
        return 'Select Source Publisher'
    } else if (!transfer.exportedAt) {
        return 'Verify & Export Works'
    } else if (transfer.workCount === 0 || transfer.workCount === undefined || transfer.workCount === null) {
        return 'Import excel works list'
    } else if (!transfer.targetPublisher) {
        return 'Select Target Publisher'
    } else if (!transfer.effectiveDate) {
        return 'Add Additional Information'
    } else if (transfer.catalogTransferStatus === 'CREATING') {
        return 'Review and Submit'
    } else {
        return ''
    }
}

const getCanReviewActions = (
    transfer: CatalogTransferView,
    opts: {
        rightsholderId: number
        isReviewTab?: boolean
    }
) => {
    const decision = getCatalogTransferDecisionForMember(transfer, opts.rightsholderId)
    return !!(
        opts.isReviewTab &&
        !isCatalogTransferDecisionFinal(decision) &&
        transfer.submittingMember.ipId != null &&
        Number(transfer.submittingMember.ipId) !== opts.rightsholderId &&
        transfer.catalogTransferStatus === 'SUBMITTED'
    )
}
